import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PawPrint, X } from 'lucide-react';
import { dismissFirstAnimalPrompt } from '../../../utils/nativeFirstAnimalPrompt';
import './NativeFirstAnimalPrompt.css';

interface NativeFirstAnimalPromptProps {
  open: boolean;
  userId: number;
  onClose: () => void;
  onCreate: () => void;
}

const NativeFirstAnimalPrompt: React.FC<NativeFirstAnimalPromptProps> = ({
  open,
  userId,
  onClose,
  onCreate,
}) => {
  const { t } = useTranslation('native', { keyPrefix: 'home.firstAnimalPrompt' });

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleDismiss = () => {
    dismissFirstAnimalPrompt(userId);
    onClose();
  };

  return createPortal(
    <div
      className='native-first-animal-prompt'
      role='dialog'
      aria-modal='true'
      aria-labelledby='native-first-animal-prompt-title'
      onClick={handleDismiss}
    >
      <div
        className='native-first-animal-prompt__sheet'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type='button'
          className='native-first-animal-prompt__close'
          onClick={handleDismiss}
          aria-label={t('close')}
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div className='native-first-animal-prompt__icon' aria-hidden='true'>
          <PawPrint size={30} strokeWidth={2} />
        </div>

        <h2 id='native-first-animal-prompt-title' className='native-first-animal-prompt__title'>
          {t('title')}
        </h2>
        <p className='native-first-animal-prompt__subtitle'>{t('subtitle')}</p>

        <button type='button' className='native-first-animal-prompt__cta' onClick={onCreate}>
          {t('cta')}
        </button>
        <button type='button' className='native-first-animal-prompt__later' onClick={handleDismiss}>
          {t('later')}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default NativeFirstAnimalPrompt;
